import { useMemo } from 'react';

/**
 * Centralized pricing config for individual apps.
 * priceId values are the real ones from Stripe.
 */
export const appsPricing = {
"lex-whentomeet": { model: 'free' },

  'email-automation': { model: 'subscription',
    monthly: { priceId: 'price_1Rrut2DiAVRhYl1XPnOyuXkJ', amount: 9.99, interval: 'month' },
    yearly: { priceId: 'price_1RrutvDiAVRhYl1X4LfHV70S', amount: 89.99, interval: 'year' },
  },

  'burner-chat': {
    model: 'subscription',
    monthly: { priceId: 'price_1Rrut2DiAVRhYl1XPnOyuXkJ', amount: 9.99, interval: 'month' },
    yearly: { priceId: 'price_1RrutvDiAVRhYl1X4LfHV70S', amount: 89.99, interval: 'year' },
  },
  'burner-call': {
    model: 'subscription',
    monthly: { priceId: 'price_1RrvQiDiAVRhYl1XPrHzgOCt', amount: 19.99, interval: 'month' },
    yearly: { priceId: 'price_1RrvRPDiAVRhYl1X3L6Vmcqf', amount: 219.99, interval: 'year' },
  },
  'lex-mail': {
    model: 'subscription',
    monthly: { priceId: 'price_1RrvghDiAVRhYl1XYR0A7TDj', amount: 14.99, interval: 'month' },
    yearly: { priceId: 'price_1RrvhPDiAVRhYl1XNoTLNPo6', amount: 149.99, interval: 'year' },
  },
};

/**
 * VIP Pass plans (shared across all mini apps)
 */
export const vipPassPlans = {
  monthly: {
    key: 'monthly',
    priceId: 'price_1RqreDDiAVRhYl1XGckbmvxX',
    amount: 19.99,
    days: 30,
    interval: 'month',
  },
  quarterly: {
    key: 'quarterly',
    priceId: 'price_1RqrfkDiAVRhYl1XeMeCFjYo',
    amount: 49.99,
    days: 90,
    interval: 'month',
    interval_count: 3,
  },
  yearly: {
    key: 'yearly',
    priceId: 'price_1RqrjwDiAVRhYl1X867Ge2wI',
    amount: 199.99,
    days: 366,
    interval: 'year',
  },
};

/**
 * Hook to interpret user.subscription structure.
 * Expects `user` shape from your auth context (with .subscriptions).
 */
// 2026-05-06 (A2): 3-day VIP grace window. Mirrors the helper in
// membershipTiers.js so callers using this hook get the same lenient
// access logic as the SuperAdmin / spotlight code paths.
const VIP_GRACE_PERIOD_MS = 3 * 24 * 60 * 60 * 1000;
function isWithinVipGrace(sub) {
  if (!sub) return false;
  const periodEndSec = Number(sub.current_period_end || sub.currentPeriodEnd || 0);
  if (!Number.isFinite(periodEndSec) || periodEndSec <= 0) return false;
  return Date.now() < periodEndSec * 1000 + VIP_GRACE_PERIOD_MS;
}

export function useSubscriptions(user) {
  return useMemo(() => {
    const vip = user?.subscriptions?.vipPass || null;
    const apps = user?.subscriptions?.apps || {};

    const isVipStrictlyActive =
      vip &&
      (vip.status === 'active' || vip.status === 'trialing') &&
      !vip.cancel_at_period_end;
    // A2: also true while inside the 3-day post-period grace window.
    const isVipActive = !!(isVipStrictlyActive || (vip && isWithinVipGrace(vip)));
    const isVipInGrace = !isVipStrictlyActive && !!(vip && isWithinVipGrace(vip));

    const isAppSubscribed = (appId) => {
      const sub = apps?.[appId];
      const strictlyActive =
        sub &&
        (sub.status === 'active' || sub.status === 'trialing') &&
        !sub.cancel_at_period_end;
      return !!(strictlyActive || (sub && isWithinVipGrace(sub)));
    };

    const getAppSub = (appId) => apps?.[appId] || null;

    return {
      isVipActive,
      isVipInGrace,                    // for UI badges: "Grace period — renews soon"
      isAppSubscribed,
      getAppSub,
      raw: { vip, apps },
    };
  }, [user]);
}
